import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiPlus,
  FiRefreshCcw,
  FiSearch,
  FiUsers,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import CustomerFilters from "./CustomerFilters";
import CustomerTable from "./CustomerTable";
import AddEditCustomer from "./AddEditCustomer";
import CustomerDrawer from "./CustomerDrawer";

import "./CustomerManagement.css";

const EMPTY_FILTERS = {
  search: "",
  status: "",
};

const EMPTY_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

const CustomerManagement = ({
  openRequirements,
}) => {
  const api = useMemo(() => new API(), []);
  const swal = useSwal();

  const [customers, setCustomers] = useState([]);

  const [filters, setFilters] =
    useState(EMPTY_FILTERS);

  const [appliedFilters, setAppliedFilters] =
    useState(EMPTY_FILTERS);

  const [pagination, setPagination] = useState(
    EMPTY_PAGINATION
  );

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [formOpen, setFormOpen] =
    useState(false);

  const [editingCustomer, setEditingCustomer] =
    useState(null);

  const [selectedCustomer, setSelectedCustomer] =
    useState(null);

  const fetchCustomers = useCallback(
    async () => {
      try {
        setLoading(true);
        setError("");

        const payload = {
          page: pagination.page,
          limit: pagination.limit,
          search:
            appliedFilters.search?.trim() ||
            "",
          status:
            appliedFilters.status || "",
        };

        const res = await api.post(
          config.getCustomers,
          payload
        );

        if (res?.status === "success") {
          const records = Array.isArray(
            res?.data
          )
            ? res.data
            : [];

          setCustomers(records);

          setPagination((previous) => ({
            ...previous,
            total: Number(
              res?.pagination?.total || 0
            ),
            page: Number(
              res?.pagination?.page ||
                previous.page
            ),
            limit: Number(
              res?.pagination?.limit ||
                previous.limit
            ),
            totalPages: Math.max(
              Number(
                res?.pagination?.totalPages ||
                  1
              ),
              1
            ),
          }));
        } else {
          setCustomers([]);

          setPagination((previous) => ({
            ...previous,
            total: 0,
            totalPages: 1,
          }));

          setError(
            res?.message ||
              "Unable to fetch customers."
          );
        }
      } catch (requestError) {
        console.error(
          "Get customers error:",
          requestError
        );

        setCustomers([]);

        setPagination((previous) => ({
          ...previous,
          total: 0,
          totalPages: 1,
        }));

        setError(
          requestError?.response?.data
            ?.message ||
            requestError?.message ||
            "Unable to fetch customers."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      appliedFilters,
      pagination.page,
      pagination.limit,
    ]
  );

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearch = () => {
    setAppliedFilters({
      ...filters,
    });

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const handleReset = () => {
    setFilters({
      ...EMPTY_FILTERS,
    });

    setAppliedFilters({
      ...EMPTY_FILTERS,
    });

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  const handleEditCustomer = (
    customer
  ) => {
    setEditingCustomer(customer);
    setFormOpen(true);
  };

  const handleViewCustomer = (
    customer
  ) => {
    setSelectedCustomer(customer);
  };

  const handleDeleteCustomer = async (
    customer
  ) => {
    const confirmation =
      await swal.warning({
        icon: "warning",
        title: "Delete customer?",
        html: `
          <div style="text-align:center">
            <p style="margin:0 0 8px">
              Are you sure you want to delete
              <strong>${customer?.customerName || "this customer"}</strong>?
            </p>
            <small style="color:#667085">
              The customer will be removed from the active list.
            </small>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
      });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      const res = await api.delete(
        config.deleteCustomerById(
          customer._id
        )
      );

      if (res?.status === "success") {
        await swal.success({
          icon: "success",
          title: "Customer Deleted",
          text:
            res?.message ||
            "Customer deleted successfully.",
          timer: 1400,
          showConfirmButton: false,
        });

        if (
          customers.length === 1 &&
          pagination.page > 1
        ) {
          setPagination((previous) => ({
            ...previous,
            page: previous.page - 1,
          }));
        } else {
          fetchCustomers();
        }
      } else {
        throw new Error(
          res?.message ||
            "Unable to delete customer."
        );
      }
    } catch (requestError) {
      swal.error({
        icon: "error",
        title: "Delete Failed",
        text:
          requestError?.response?.data
            ?.message ||
          requestError?.message ||
          "Unable to delete customer.",
      });
    }
  };

  const handleChangeCustomerStatus =
    async (
      customer,
      nextStatus
    ) => {
      try {
        const res = await api.patch(
          config.changeCustomerStatusById(
            customer._id
          ),
          {
            status: nextStatus,
          }
        );

        if (res?.status === "success") {
          await swal.success({
            icon: "success",
            title: "Status Updated",
            text:
              res?.message ||
              "Customer status updated successfully.",
            timer: 1300,
            showConfirmButton: false,
          });

          fetchCustomers();
        } else {
          throw new Error(
            res?.message ||
              "Unable to update customer status."
          );
        }
      } catch (requestError) {
        swal.error({
          icon: "error",
          title: "Update Failed",
          text:
            requestError?.response?.data
              ?.message ||
            requestError?.message ||
            "Unable to update customer status.",
        });
      }
    };

  const handleFormSuccess = (
    savedCustomer
  ) => {
    setFormOpen(false);
    setEditingCustomer(null);

    if (savedCustomer?._id) {
      setSelectedCustomer(savedCustomer);
    }

    fetchCustomers();
  };

  const handlePageChange = (
    page
  ) => {
    if (
      page < 1 ||
      page > pagination.totalPages
    ) {
      return;
    }

    setPagination((previous) => ({
      ...previous,
      page,
    }));
  };

  const handleLimitChange = (
    event
  ) => {
    setPagination((previous) => ({
      ...previous,
      page: 1,
      limit: Number(
        event.target.value
      ),
    }));
  };

  return (
    <div className="customer-management-page">
      <div className="customer-management-header">
        <div>
          {/* <span className="customer-management-heading-label">
            CUSTOMER MANAGEMENT
          </span> */}

          <h3>
            All Customers
          </h3>

          {/* <p>
            Add, search, update and manage
            customer information.
          </p> */}
        </div>

        <div className="customer-management-header-actions">
          <button
            type="button"
            className="customer-management-refresh-button"
            onClick={fetchCustomers}
            disabled={loading}
            title="Refresh customers"
          >
            <FiRefreshCcw
              className={
                loading
                  ? "customer-management-refresh-icon spinning"
                  : "customer-management-refresh-icon"
              }
            />
          </button>

          <button
            type="button"
            className="customer-management-add-button"
            onClick={
              handleAddCustomer
            }
          >
            <FiPlus />
            Add Customer
          </button>
        </div>
      </div>

      <div className="customer-management-summary">
        <div className="customer-management-summary-icon">
          <FiUsers />
        </div>

        <div>
          <span>
            Total Customers
          </span>

          <strong>
            {pagination.total}
          </strong>
        </div>
      </div>

      <CustomerFilters
        filters={filters}
        setFilters={setFilters}
        loading={loading}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {error ? (
        <div className="customer-service-error">
          {error}
        </div>
      ) : null}

      <CustomerTable
        customers={customers}
        loading={loading}
        pagination={pagination}
        onView={
          handleViewCustomer
        }
        onEdit={
          handleEditCustomer
        }
        onDelete={
          handleDeleteCustomer
        }
        onChangeStatus={
          handleChangeCustomerStatus
        }
        onPageChange={
          handlePageChange
        }
        onLimitChange={
          handleLimitChange
        }
        onAddCustomer={
          handleAddCustomer
        }
      />

      <AddEditCustomer
        open={formOpen}
        customer={
          editingCustomer
        }
        onClose={() => {
          setFormOpen(false);
          setEditingCustomer(null);
        }}
        onSuccess={
          handleFormSuccess
        }
      />

      <CustomerDrawer
        open={Boolean(
          selectedCustomer
        )}
        customer={
          selectedCustomer
        }
        onClose={() =>
          setSelectedCustomer(null)
        }
        onEdit={(customer) => {
          setSelectedCustomer(null);
          handleEditCustomer(
            customer
          );
        }}
        onOpenRequirements={() => {
          setSelectedCustomer(null);

          if (
            typeof openRequirements ===
            "function"
          ) {
            openRequirements();
          }
        }}
      />
    </div>
  );
};

export default CustomerManagement;